import { INLINE_COLOR_PATTERN } from '@core/constants';

/**
 * Checks if a color value is a default black color.
 * @param color - The color value to check
 * @returns True if the color is a default black color variant
 */
export function isDefaultBlackColor(color?: string | null): boolean {
	if (!color) return false;

	const normalized = color.toLowerCase().replace(/\s+/g, '').replace(/;$/, '');

	return (
		normalized === 'black' ||
		normalized === '#000' ||
		normalized === '#000000' ||
		normalized === 'rgb(0,0,0)' ||
		normalized === 'rgba(0,0,0,1)' ||
		normalized === 'rgba(0,0,0,1.0)'
	);
}

/**
 * Removes default black color styling from HTML content to prevent overriding theme colors.
 * Strips black color values from inline styles and font color attributes.
 *
 * Only strips when `pageTextColor` is provided AND is itself non-black.
 * Rationale: if the page theme color is non-black (e.g. #333333), explicit black in
 * content should be removed so the theme color cascades through. But if the page
 * theme color is black (the default), explicit black inline styles should be kept so
 * they survive email forwarding (forwards strip <head> style blocks and break
 * CSS inheritance chains).
 *
 * @param content - The HTML content string to process
 * @param pageTextColor - The page-level text-color value from IPage data.value
 * @returns The processed HTML content with default black colors removed (when appropriate)
 */
export function replaceInlineColorBeforeRender(
	content: string,
	pageTextColor?: string | null,
): string {
	// If no page color is supplied, or the page color is itself black, do not strip —
	// preserve explicit black so it survives email forwarding.
	if (!pageTextColor || isDefaultBlackColor(pageTextColor)) return content;

	if (!content || !INLINE_COLOR_PATTERN.test(content)) return content;

	// DOMParser is only available in browser environments
	if (typeof DOMParser === 'undefined') {
		// In Node.js/SSR environments, return content unchanged
		// Color stripping will happen client-side when the content is rendered
		return content;
	}

	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(`<div>${content}</div>`, 'text/html');
		const root = doc.body.firstElementChild as HTMLElement | null;
		if (!root) return content;

		root.querySelectorAll<HTMLElement>('[style], font[color]').forEach(node => {
			if (node.tagName.toLowerCase() === 'font') {
				const fontColor = node.getAttribute('color');
				if (isDefaultBlackColor(fontColor)) {
					node.removeAttribute('color');
				}
			}

			if (node.hasAttribute('style')) {
				if (isDefaultBlackColor(node.style.color)) {
					node.style.removeProperty('color');
				}

				const style = node.getAttribute('style');
				if (!style || style.trim().length === 0) {
					node.removeAttribute('style');
				}
			}
		});

		return root.innerHTML;
	} catch {
		return content;
	}
}
